import { InteractiveList } from '@nimbus-ds/patterns';
import { i18nResponseDto } from '@tiendanube/common';
import { AiFeature } from '@tiendanube/common/src/enums';
import { AI_PRODUCT_IMAGE_SMART_ERASE_ENABLED } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { DEFAULT_LANGUAGE, LanguagesType } from 'App/i18n';
import { useAiUsage } from 'domains/Ai/hooks';
import {
  trackingAiEditImageReplaceBgWithColorCtaClick,
  trackingAiEditImageSmartEraseCtaClick,
} from 'domains/Ai/tracking';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import ChangeBackgroundModal from './components/ChangeBackgroundModal';
import EditAltTextModal from './components/EditAltTextModal';
import { AltTextGenerationContext } from './components/EditAltTextModal/components/AltTextGenerationModal';
import ModalInteractiveListAction from './components/ModalInteractiveListAction';
import SmartEraseModal from './components/SmartEraseModal';
import { ImageGalleryState } from '../../../ImageGallery';
import { EditedImage } from '../EditImageModal';

interface EditImageActionsProps {
  language?: LanguagesType;
  image: ImageGalleryState;
  altTextGenerationContext: AltTextGenerationContext;
  onChangeAltText: (value: i18nResponseDto) => void;
  onSaveImage: (image: EditedImage) => void;
}

function EditImageActions({
  language = DEFAULT_LANGUAGE,
  image,
  altTextGenerationContext,
  onChangeAltText,
  onSaveImage,
}: Readonly<EditImageActionsProps>) {
  const t = useTranslationCatalog('products.modal.editPhoto.actions');
  const defaultValue = image.alt;

  const hasAltText =
    defaultValue[language] !== undefined && defaultValue[language] !== '';

  const [smartEraseEnabled] = useHasTags([
    AI_PRODUCT_IMAGE_SMART_ERASE_ENABLED,
  ]);

  const { isLoadingAiUses, requiredTier } = useAiUsage();

  return (
    <InteractiveList>
      {!isLoadingAiUses && (
        <ModalInteractiveListAction
          cta={t('changeBackground.cta')}
          aIPoweredAction
          isBeta
          upSellProps={{
            trackingSource: AiFeature.AI_IMAGES_REPLACE_BG_COLOR,
            requiredTier,
          }}
          onOpen={() => trackingAiEditImageReplaceBgWithColorCtaClick()}
          modalChildren={
            <ChangeBackgroundModal
              currentImage={image}
              onSaveImage={onSaveImage}
            />
          }
        />
      )}
      {smartEraseEnabled && !isLoadingAiUses && (
        <ModalInteractiveListAction
          cta={t('smartErase.cta')}
          aIPoweredAction
          isBeta
          upSellProps={{
            trackingSource: AiFeature.AI_IMAGES_SMART_ERASE,
            requiredTier,
          }}
          onOpen={() => trackingAiEditImageSmartEraseCtaClick()}
          modalChildren={
            <SmartEraseModal currentImage={image} onSaveImage={onSaveImage} />
          }
        />
      )}
      <ModalInteractiveListAction
        cta={hasAltText ? t('altText.ctaEdit') : t('altText.ctaAdd')}
        modalChildren={
          <EditAltTextModal
            image={image}
            onChange={onChangeAltText}
            altTextGenerationContext={altTextGenerationContext}
          />
        }
      />
    </InteractiveList>
  );
}

export default EditImageActions;
